import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { VaTextarea } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import vaTextareaFieldMapping from 'platform/forms-system/src/js/web-component-fields/vaTextareaFieldMapping';
import { ERRORED_CATEGORY_PREDICTION } from '../../../constants';

const MAX_LENGTH = 3000;

// Replaces the built-in charcount message so it can read "Remaining characters: N"
const QuestionTextareaField = props => {
  const mappedProps = vaTextareaFieldMapping(props);
  const length = props.childrenProps?.formData?.length || 0;
  const remaining = Math.max(MAX_LENGTH - length, 0);
  const textareaRef = useRef(null);

  // The native drag handle sets a fixed pixel width that can't shrink with the
  // viewport, so only allow vertical resizing
  useEffect(() => {
    const host = textareaRef.current;
    if (!host?.componentOnReady) return;
    host.componentOnReady().then(() => {
      const textarea = host.shadowRoot?.querySelector('textarea');
      if (textarea) textarea.style.resize = 'vertical';
    });
  }, []);

  return (
    <>
      <VaTextarea
        {...mappedProps}
        ref={textareaRef}
        charcount={false}
        maxlength={MAX_LENGTH}
      />
      <span
        className="vads-u-display--block vads-u-color--gray-medium vads-u-margin-top--1"
        data-testid="question-remaining-characters"
      >
        Remaining characters: {remaining.toLocaleString('en-US')}
      </span>
    </>
  );
};

QuestionTextareaField.propTypes = {
  childrenProps: PropTypes.shape({
    formData: PropTypes.string,
  }),
};

const SimpleQuestionDescription = () => (
  <div className="ask-va-simple-question-description">
    <h3 className="vads-u-display--flex vads-u-align-items--flex-start vads-u-margin-top--2 vads-u-margin-bottom--0">
      <va-icon
        icon="auto_awesome"
        size={3}
        class="vads-u-margin-right--1"
        aria-hidden="true"
      />
      What do you need help with?
    </h3>
    <p className="vads-u-margin-top--2 vads-u-margin-bottom--0">
      In a few words, describe what you need help with. We’ll suggest categories
      using AI that may match your question. You can enter questions or phrases
      like these:
    </p>
    <ul>
      <li>Check my claim status</li>
      <li>Do I qualify for the additional 12 months of GI Bill benefits?</li>
      <li>Pay my copay bill</li>
      <li>Remove a dependent from my benefits</li>
    </ul>
    <p className="vads-u-margin-bottom--2">
      You’ll have a chance to include more details about your question later in
      this form.
    </p>
  </div>
);

const simpleQuestionPage = {
  uiSchema: {
    'ui:description': SimpleQuestionDescription,
    question: {
      'ui:title': 'Describe what you need help with',
      'ui:webComponentField': QuestionTextareaField,
      'ui:required': () => true,
      'ui:errorMessages': {
        required: 'Enter a brief description of what you need help with',
      },
    },
  },
  schema: {
    type: 'object',
    required: [],
    properties: {
      question: {
        type: 'string',
        pattern: /\S/, // At least one non-whitespace character
        minLength: 1,
        maxLength: MAX_LENGTH,
      },
    },
  },
  // A new question invalidates any prior suggestions and the category picked from them
  updateFormData: (oldFormData, newFormData) => {
    if (oldFormData.question === newFormData.question) return newFormData;
    return {
      ...newFormData,
      categoryId: null,
      selectCategory: '',
      categoryAllowAttachments: false,
      categoryRequiresSignIn: false,
      requireSignInLogic: {
        ...newFormData.requireSignInLogic,
        category: false,
      },
      contactPreferences: [],
      predictions: {
        ...newFormData.predictions,
        category: ERRORED_CATEGORY_PREDICTION,
      },
    };
  },
};

export default simpleQuestionPage;
